import React from "react";
import { Link } from "react-router-dom";
import { Check, FlaskConical, Dumbbell, HeartPulse, Crown, ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { GOLD_STANDARD, IMG, chalkitLink } from "../data/mock";
import { LabsCards } from "../components/LabsCards";
import { QuickAnswer } from "../components/QuickAnswer";

const GOLD_SERVICE = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://bostonirongrittraining.com/gold-standard#service",
  name: "Gold Standard Programs — Boston Iron Grit",
  serviceType: "Premium Personal Training and Longevity Coaching",
  description: "Premium coaching tiers combining personal training, baseline labs, body composition testing and provider-guided longevity protocols including peptides and GLP-1s. Built to accommodate the 50+ athlete.",
  provider: { "@id": "https://bostonirongrittraining.com/#business" },
  areaServed: { "@type": "Place", name: "West Roxbury, Boston, MA" },
  url: "https://bostonirongrittraining.com/gold-standard",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Gold Standard Tiers",
    itemListElement: [
      { "@type": "Offer", name: "The Foundation", price: "1985", priceCurrency: "USD", description: "3-month program. Personal training sessions, baseline labs and body composition, the 8 daily habits and a dedicated coach." },
      { "@type": "Offer", name: "The Ascension", price: "2525", priceCurrency: "USD", description: "3-month program. More sessions, deeper testing and regular protocol reviews with medical partners." },
      { "@type": "Offer", name: "The Elite Performance", price: "6800", priceCurrency: "USD", description: "6-month program. Complete support with provider-guided longevity protocols." },
    ],
  },
};

const GOLD = "#D4AF37";
const GOLD_LIGHT = "#E8C766";

const goldFaqs = [
  {
    q: "What are the Gold Standard Programs?",
    a: "The Gold Standard Programs — The Foundation, The Ascension and The Elite — are Boston Iron Grit's premium coaching tiers built around performance, longevity and avidity. Every tier includes personal training and labs, accommodates the 50+ athlete, and is the best place to start for anyone interested in peptides and GLP-1s.",
  },
  {
    q: "How much do the Gold Standard Programs cost?",
    a: "The Foundation is $1,985 (save $200) and The Ascension is $2,525 (save $460) — both 3-month programs. The Elite Performance is $6,800 (save $2,820) for six months of complete support. Every graduate unlocks unlimited group classes at $225/month, and higher tiers add ongoing savings on labs, peptides and supplements.",
  },
  {
    q: "Are the Gold Standard Programs suitable for athletes over 50?",
    a: "Yes — every tier is built to accommodate the 50+ athlete. Programming, loading and recovery are matched to your health history and lab work, not your birth year.",
  },
  {
    q: "Is this where I should start if I'm interested in peptides or GLP-1s?",
    a: "Yes. The Gold Standard Programs are the best entry point: baseline labs and a body composition scan come first, then eligibility screening and consultations run through our licensed medical partners, with coaching support alongside any protocol.",
  },
];

export default function GoldStandard() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Gold Standard Programs", path: "/gold-standard" },
  ];

  const pillars = [
    { icon: Dumbbell, title: "Personal Training Included", text: "Every tier is anchored by one-on-one coaching — technique, load and pacing dialed to you." },
    { icon: FlaskConical, title: "Labs & Body Composition", text: "Baseline blood panels and InBody scans so every decision is driven by your data." },
    { icon: HeartPulse, title: "Built for the 50+ Athlete", text: "All programs accommodate the 50+ athlete — train for the next 30 years, not the next 30 days." },
    { icon: Crown, title: "Peptides & GLP-1 Gateway", text: "The best place to start — eligibility screening and protocols through licensed medical partners." },
  ];

  const steps = [
    { n: "01", t: "Free Goal Review", d: "A 20-minute conversation about your goals, history and health priorities. No workout, no pressure." },
    { n: "02", t: "Baseline: Labs + InBody", d: "A blood panel and body composition scan set your starting line and inform your tier." },
    { n: "03", t: "Your Matched Tier", d: "We recommend Foundation, Ascension or Elite — matched to your goals and labs, priced at your review." },
  ];

  return (
    <>
      <Seo
        title="Gold Standard Programs | Boston Iron Grit"
        description="Gold Standard Programs in West Roxbury: The Foundation $1,985, The Ascension $2,525, The Elite Performance $6,800. Personal training and labs included."
        path="/gold-standard"
        schemas={[breadcrumbSchema(crumbs), faqSchema(goldFaqs), GOLD_SERVICE]}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.goldLift})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/88 to-[#0A0A0B]/45" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-28">
          <Reveal>
            <Crumbs items={crumbs} />
            <div className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] px-3 py-1.5 border" style={{ color: GOLD_LIGHT, borderColor: `${GOLD}4D`, backgroundColor: `${GOLD}14` }}>
              Performance · Longevity · Avidity
            </div>
            <h1 className="mt-5 font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight max-w-3xl" data-testid="gold-hero-title">
              Gold Standard<br /><span style={{ color: GOLD }}>Programs.</span>
            </h1>
            <p className="mt-7 text-neutral-200 text-base lg:text-lg leading-relaxed max-w-2xl border-l-2 pl-5" style={{ borderColor: GOLD }}>
              {GOLD_STANDARD.blurb}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/get-started"
                data-testid="gold-cta-primary"
                className="inline-flex items-center justify-center gap-2 text-[#0A0A0B] font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-all duration-200 hover:translate-y-[-2px]"
                style={{ backgroundColor: GOLD }}
              >
                Start With a Free Goal Review <ArrowRight size={15} />
              </Link>
              <BtnGhost to="/live-big-365">Explore Live B.I.G. 365</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Quick answer */}
      <section className="bg-[#0A0A0B] pt-10">
        <QuickAnswer>
          The Gold Standard Programs at Boston Iron Grit are three premium coaching tiers: The Foundation ($1,985, 3 months), The Ascension
          ($2,525, 3 months) and The Elite Performance ($6,800, 6 months). All include personal training and labs, accommodate the 50+
          athlete, and are the entry point for peptides and GLP-1s through licensed providers.
        </QuickAnswer>
      </section>

      {/* Tiers */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Three Tiers</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
              Take your training<br /><span className="text-neutral-600">to the next level.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {GOLD_STANDARD.tiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 90}>
                <div
                  data-testid={`gold-tier-${t.name.toLowerCase().replace(/\s+/g, "-").replace("the-", "")}`}
                  className={`relative flex flex-col h-full p-7 transition-all duration-300 hover:translate-y-[-4px] ${
                    t.featured ? "bg-[#141416] border-2" : "bg-[#121214] border border-white/8 hover:border-white/20"
                  }`}
                  style={t.featured ? { borderColor: GOLD, boxShadow: `0 20px 60px ${GOLD}26` } : undefined}
                >
                  {t.featured && (
                    <div className="absolute -top-3.5 left-6 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 text-[#0A0A0B] font-semibold" style={{ backgroundColor: GOLD }}>
                      Most Chosen
                    </div>
                  )}
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">Tier {String(i + 1).padStart(2, "0")} · {t.term}</p>
                  <h3 className="mt-3 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">
                    {t.name}<span style={{ color: GOLD }}>.</span>
                  </h3>
                  <div className="mt-4 flex items-baseline gap-2.5 flex-wrap">
                    <span className="font-heading font-extrabold text-4xl text-white" data-testid={`gold-price-${t.name.toLowerCase().replace(/\s+/g, "-").replace("the-", "")}`}>{t.price}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] px-2 py-1 border font-semibold" style={{ color: GOLD_LIGHT, borderColor: `${GOLD}55`, backgroundColor: `${GOLD}12` }}>{t.save}</span>
                  </div>
                  <p className="mt-4 text-[13px] text-neutral-400 leading-relaxed">{t.detail}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {t.includes.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
                        <span className="text-[13px] text-neutral-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-[11px] text-neutral-500 leading-relaxed border-t border-white/5 pt-3">{t.after}</p>
                  <Link
                    to="/get-started"
                    className={`mt-5 inline-flex items-center justify-center font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-all duration-200 ${
                      t.featured ? "text-[#0A0A0B] hover:translate-y-[-1px]" : "border border-white/20 hover:border-white/60 text-white"
                    }`}
                    style={t.featured ? { backgroundColor: GOLD } : undefined}
                  >
                    Start With a Free Goal Review
                  </Link>
                  <a
                    href={chalkitLink(t.chalkitKey)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`gold-buy-${t.name.toLowerCase().replace(/\s+/g, "-").replace("the-", "")}`}
                    className="mt-3 inline-flex items-center justify-center font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500 hover:text-white transition-colors"
                  >
                    Or purchase now via ChalkIt Pro →
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500" data-testid="gold-pricing-note">{GOLD_STANDARD.note}</p>
          <Reveal delay={150}>
            <div className="mt-10 p-8 lg:p-10 bg-[#141416] border border-[#D4AF37]/35 grid lg:grid-cols-[1fr_auto] gap-8 items-center" data-testid="gold-platinum">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: GOLD_LIGHT }}>// Membership</p>
                <h3 className="mt-3 font-heading font-extrabold uppercase text-2xl lg:text-3xl text-white tracking-tight">
                  {GOLD_STANDARD.platinum.name} — {GOLD_STANDARD.platinum.price}<span className="text-neutral-500 text-lg">{GOLD_STANDARD.platinum.per}</span>
                </h3>
                <p className="mt-3 text-[14px] text-neutral-400 leading-relaxed max-w-2xl">{GOLD_STANDARD.platinum.detail}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <BtnPrimary href={chalkitLink("unlimited")} data-testid="gold-platinum-join">Join Platinum</BtnPrimary>
                <BtnGhost to="/pricing">See All Memberships</BtnGhost>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Every tier includes */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Every Tier Includes</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-10">
              The gold standard, standard.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="p-7 bg-[#121214] border border-white/5 hover:border-[#D4AF37]/40 transition-colors h-full">
                  <p.icon size={22} style={{ color: GOLD }} />
                  <h3 className="mt-5 font-heading font-bold uppercase text-lg text-white tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-[12px] text-neutral-600 leading-relaxed max-w-3xl">
            Medical services — labs, peptides and GLP-1s — run through licensed providers who determine eligibility. Gold Standard coaching is not medical care. Always consult your physician about medical decisions.
          </p>
          <Link to="/peptides-glp1-boston" data-testid="gold-peptides-link" className="mt-4 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-300 hover:text-white transition-colors">
            How our provider-guided peptide & GLP-1 program works <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-10">
              Start with a free goal review
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <div className="relative p-7 bg-[#121214] border border-white/5 h-full">
                  <div className="font-heading font-extrabold text-5xl" style={{ color: `${GOLD}26` }}>{s.n}</div>
                  <h3 className="mt-3 font-heading font-bold uppercase text-xl text-white tracking-tight">{s.t}</h3>
                  <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LABS */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20" data-testid="gold-labs">
        <div className="max-w-7xl mx-auto px-6">
          <LabsCards idPrefix="gold" />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={goldFaqs} title="Gold Standard — FAQs" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Ready for the gold standard?"
        sub="Book a free goal review — we'll set your baseline, match your tier and give you an exact price. No pressure, no obligation."
      />
    </>
  );
}
